import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import i18n from 'i18n-js';

import { scale } from '@components/frame';
import { splitIPAndPort } from '@helpers';

import TouchableElement from '@components/common/touchableElement';
import { SmallLoading } from '@components/loading';

import { font } from '@styles/fonts';
import Button from '@styles/buttons';
import Svg from '@styles/icons/svg';
import { modalFieldBg, SCREEN_BORDER_RADIUS } from '@v2/pages/common/constants/base.constants';

export default ({ C, goBack, setData, setMode, MODES, maxHeight, startMachineOnboardingSession, getDemoMachineType, addMachineAction, machines }) => {
  const [loading, setLoading] = useState(false);
  const [localStatusStorage, setLocalStatusStorage] = useState({});
  const [machinesList, setMachineList] = useState([]);
  const [machineLoaded, setMachineLoaded] = useState(false);
  const [machineError, setMachineError] = useState(false);

  useEffect(() => {
    (async () => {
      setMachineLoaded(false);
      setMachineError(false);
      const data = await getDemoMachineType();
      const demoMachineTypeIds = machines.machines.map((i) => i.demoMachineTypeId);
      if (data) {
        setMachineList(data.filter((i) => !demoMachineTypeIds.includes(i.demoMachineTypeId)).map((i) => ({ ...i, isReachable: true })));
      } else {
        setMachineError(true);
      }
      setMachineLoaded(true);
    })();
  }, []);

  const renderAction = (item) => {
    if (item.endpoint == loading) {
      return <SmallLoading />;
    }

    if (item.isReachable) {
      return (
        <Button
          {...{
            disabled: loading,
            variant: 'S',
            type: 'Secondary',
            onPress: async () => {
              if (loading) {
                return;
              }
              setLoading(item.endpoint);
              const machineOnboardingSessionId = await startMachineOnboardingSession({ endpoint: item.endpoint }, true);
              if (machineOnboardingSessionId) {
                const machine = { ...item, machineOnboardingSessionId, isDemoMachine: true };
                setData(machine);
                await addMachineAction(machine);
              }

              setLocalStatusStorage({
                ...localStatusStorage,
                [item.endpoint]: {
                  isReachable: false,
                },
              });

              setLoading(false);
            },
          }}
        >
          {i18n.t('Connect')}
        </Button>
      );
    }
  };

  const getMachineStatus = (machine) => {
    if (!machine.isReachable) {
      return { text: 'Connecting to the machine failed because of a timeout.', color: 'Label/Light/Error' };
    }

    if (machine.loading) {
      return { text: 'Connecting...', color: 'Label/Light/Primary' };
    }

    return { text: 'Online', color: 'System/Light/Green' };
  };

  const headerHeight = scale(58);

  return (
    <View style={{}}>
      <View>
        <View
          style={{
            width: '100%',
            zIndex: 1000,
            height: headerHeight,
          }}
        >
          <View style={{ position: 'absolute', zIndex: 1000, left: 0 }}>
            <TouchableElement onPress={() => goBack()}>
              {({ boxStyle, pressHoverColor }) => (
                <View style={[boxStyle, { padding: scale(15), flexDirection: 'row' }]}>
                  <View style={{ marginTop: scale(2) }}>
                    <Svg icon="chevronLeft" width={scale(11)} height={scale(17)} color="Link / Light / Primary" />
                  </View>
                  <Text style={[{ color: C(pressHoverColor('System/Light/Blue')), marginLeft: scale(7) }, font.Body]}>{i18n.t('Back')}</Text>
                </View>
              )}
            </TouchableElement>
          </View>
        </View>
        <View style={{ width: '100%', position: 'absolute', zIndex: 100, top: scale(15) }}>
          <Text style={[{ color: C('Label/Light/Primary'), width: '100%', textAlign: 'center' }, font.Headline]}>{i18n.t('Add Demo Machine')}</Text>
        </View>
      </View>
      <View style={{ paddingTop: scale(20), maxHeight: maxHeight - headerHeight }}>
        <View style={{ paddingHorizontal: scale(30), flexDirection: 'row' }}>
          <Text style={[{ color: C('Label/Light/Tertiary') }, font.Footnote]}>
            {i18n.t(machineLoaded && !machinesList.length ? 'NO MACHINES FOUND' : 'SEARCHING FOR MACHINES')}
          </Text>
          {!machineLoaded && !machineError && (
            <View style={{ marginLeft: scale(7) }}>
              <SmallLoading />
            </View>
          )}
        </View>
        <ScrollView contentContainerStyle={{ paddingBottom: scale(18) }}>
          <View style={{ paddingHorizontal: scale(15), marginTop: scale(7) }}>
            {machineError ? (
              <View style={{ paddingHorizontal: scale(15), paddingVertical: scale(50) }}>
                <Text style={[{ color: C('Label/Light/Tertiary'), width: '100%', textAlign: 'center' }, font.Footnote]}>
                  {i18n.t('Problem with receiving machine list')}
                </Text>
              </View>
            ) : (
              machinesList.map((item, index) => {
                const localStatus = localStatusStorage[item.endpoint] || {};
                const mappedItem = {
                  ...item,
                  ...localStatus,
                };
                const status = getMachineStatus(mappedItem);

                return (
                  <View
                    key={item.endpoint}
                    style={[
                      {
                        height: scale(72),
                        width: '100%',
                        flexDirection: 'row',
                        paddingVertical: scale(7),
                        borderBottomWidth: StyleSheet.hairlineWidth,
                        borderBottomColor: C('Hairline/Light/No trans'),
                        backgroundColor: C(modalFieldBg),
                      },
                      index === 0 && {
                        borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
                        borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
                      },
                      index === machinesList.length - 1 && {
                        borderBottomWidth: 0,
                        borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS),
                        borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS),
                      },
                    ]}
                  >
                    <View style={{ paddingLeft: scale(18) }}>
                      <View style={{ width: scale(125) }}>
                        <Text
                          style={[{ color: C('Label/Light/Primary') }, font.Body, !item.fullName && { textTransform: 'capitalize' }]}
                          numberOfLines={1}
                        >
                          {item.fullName || item.machineBrandCode}{' '}
                        </Text>
                      </View>
                      <Text style={[{ color: C('Label/Light/Tertiary') }, font.Footnote]}>{i18n.t(splitIPAndPort(item.endpoint).ipAddress)}</Text>
                      <View style={{ flexDirection: 'row' }}>
                        <Text style={[{ color: C(status?.color) }, font.Footnote]} numberOfLines={1} ellipsizeMode={'middle'}>
                          {i18n.t(status?.text)}
                          {status?.ncFile && (
                            <Text style={[{ color: C('Label/Light/Quaternary') }, font['Caption 2']]}>
                              {'  '}
                              {status?.ncFile}
                            </Text>
                          )}
                        </Text>
                      </View>
                    </View>
                    <View
                      style={{
                        position: 'absolute',
                        top: scale(mappedItem.isDemo ? 30 : 13),
                        right: scale(mappedItem.isDemo ? 16 : 9),
                      }}
                    >
                      {renderAction(mappedItem)}
                    </View>
                  </View>
                );
              })
            )}
          </View>
          <View style={{ paddingHorizontal: scale(15), marginTop: scale(20) }}>
            <TouchableElement
              onPress={() => {
                setData({});
                setMode(MODES.ADD_MACHINE_IP);
              }}
            >
              {({ boxStyle, pressHoverColor }) => (
                <View
                  style={[
                    boxStyle,
                    {
                      height: scale(42),
                      width: '100%',
                      paddingHorizontal: scale(15),
                      justifyContent: 'center',
                      backgroundColor: C(pressHoverColor(modalFieldBg)),
                      borderRadius: scale(SCREEN_BORDER_RADIUS),
                    },
                  ]}
                >
                  <Text style={[{ color: C('Link/Light/Primary') }, font.Body]}>{i18n.t('Add manually')}...</Text>
                </View>
              )}
            </TouchableElement>
          </View>
          <View style={{ paddingHorizontal: scale(15), marginTop: scale(20) }}>
            <TouchableElement
              onPress={() => {
                setData({});
                setMode(MODES.SEARCH_SETTINGS);
              }}
            >
              {({ boxStyle, pressHoverColor }) => (
                <View
                  style={[
                    boxStyle,
                    {
                      height: scale(42),
                      width: '100%',
                      paddingHorizontal: scale(15),
                      justifyContent: 'center',
                      backgroundColor: C(pressHoverColor(modalFieldBg)),
                      borderRadius: scale(SCREEN_BORDER_RADIUS),
                    },
                  ]}
                >
                  <Text style={[{ color: C('Link/Light/Primary') }, font.Body]}>{i18n.t('Search Settings')}...</Text>
                </View>
              )}
            </TouchableElement>
          </View>
        </ScrollView>
      </View>
    </View>
  );
};
